import React, { useState, useEffect } from "react";
import { Table, Card, Button, Input, Select, Space, Tag, Popconfirm, message, Row, Col, Tooltip, Modal } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CopyOutlined, SendOutlined } from "@ant-design/icons";
import { emailTemplateService } from "../../../api/enhancedApi";
import { EMAIL_TEMPLATE_CONSTANTS } from "../../../api/emailTemplateApi";
import EmailTemplateEditor from "./EmailTemplateEditor";
import EmailTemplateDetail from "./EmailTemplateDetail";

const { Option } = Select;

const EmailTemplateList = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    // 筛选条件
    const [filters, setFilters] = useState({
        search: "",
        category: undefined,
        status: undefined,
        is_public: undefined,
        tags: undefined,
    });

    // 模态框状态
    const [editorVisible, setEditorVisible] = useState(false);
    const [detailVisible, setDetailVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [viewingTemplate, setViewingTemplate] = useState(null);

    // 分类列表和标签列表
    const [categories, setCategories] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);

    useEffect(() => {
        loadTemplates();
    }, [pagination.current, pagination.pageSize, filters]);

    useEffect(() => {
        loadCategories();
        loadAvailableTags();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.current,
                page_size: pagination.pageSize,
                ...filters,
            };

            // 处理 tags 参数：如果是数组，转换为逗号分隔的字符串
            if (params.tags && Array.isArray(params.tags)) {
                params.tags = params.tags.join(",");
            }

            const response = await emailTemplateService.getTemplates(params);
            setTemplates(response.data.items);
            setPagination((prev) => ({
                ...prev,
                total: response.data.total,
            }));
        } catch (error) {
            message.error("加载模板列表失败");
            console.error("Load templates error:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await emailTemplateService.getCategories();
            setCategories(response.data);
        } catch (error) {
            console.error("Load categories error:", error);
        }
    };

    const loadAvailableTags = async () => {
        try {
            const response = await emailTemplateService.getAvailableTags();
            // 统一标签系统后，API 返回 {tags: [...]} 格式
            setAvailableTags(response.data.tags || []);
        } catch (error) {
            message.error("加载可用标签失败");
            console.error("Load available tags error:", error);
        }
    };

    const handleTableChange = (pagination) => {
        setPagination((prev) => ({
            ...prev,
            current: pagination.current,
            pageSize: pagination.pageSize,
        }));
    };

    const handleSearch = (value) => {
        setFilters((prev) => ({ ...prev, search: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleCreate = () => {
        setEditingTemplate(null);
        setEditorVisible(true);
    };

    const handleEdit = async (template) => {
        try {
            // 获取完整的模板数据
            const response = await emailTemplateService.getTemplateById(template.id);
            setEditingTemplate(response.data);
            setEditorVisible(true);
        } catch (error) {
            message.error("获取模板详情失败");
            console.error("Get template detail error:", error);
        }
    };

    const handleView = (template) => {
        setViewingTemplate(template);
        setDetailVisible(true);
    };

    const handleDelete = async (templateId) => {
        try {
            await emailTemplateService.deleteTemplate(templateId);
            message.success("模板删除成功");
            loadTemplates();
        } catch (error) {
            message.error("删除模板失败");
            console.error("Delete template error:", error);
        }
    };

    const handleCopy = async (template) => {
        try {
            // 1. 先根据ID获取模板的完整详情
            const response = await emailTemplateService.getTemplateById(template.id);
            const fullTemplate = response.data;

            // 2. 使用完整详情创建副本
            const newTemplateData = {
                ...fullTemplate,
                template_code: `${fullTemplate.template_code}_copy_${Date.now()}`,
                name: `${fullTemplate.name} (副本)`,
                status: "DRAFT",
            };

            // 3. 删除后端会自动生成的字段
            delete newTemplateData.id;
            delete newTemplateData.created_at;
            delete newTemplateData.updated_at;
            delete newTemplateData.created_by;
            delete newTemplateData.updated_by;
            delete newTemplateData.usage_count;
            delete newTemplateData.last_used_at;
            delete newTemplateData.is_system;

            // 4. 调用创建接口
            await emailTemplateService.createTemplate(newTemplateData);

            message.success("模板复制成功");
            loadTemplates(); // 重新加载列表
        } catch (error) {
            const errorMessage = error.response?.data?.detail || "复制模板失败";
            message.error(errorMessage);
            console.error("Copy template error:", error);
        }
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            ACTIVE: { color: "green", text: "启用" },
            INACTIVE: { color: "red", text: "禁用" },
            DRAFT: { color: "orange", text: "草稿" },
        };
        const config = statusConfig[status] || { color: "default", text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getCategoryText = (category) => {
        const categoryMap = {
            FLIGHT_OPERATION: "航班运行",
            FUEL_REQUEST: "加油申请",
            CATERING_ORDER: "餐食订单",
            CREW_TRANSPORTATION: "机组交通",
            HOTAC: "酒店住宿",
            PERMIT_REQUEST: "许可申请",
            GENERAL: "通用",
        };
        return categoryMap[category] || category;
    };

    const columns = [
        {
            title: "序号",
            key: "index",
            width: 40,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "模板代码",
            dataIndex: "template_code",
            key: "template_code",
            width: 120,
            ellipsis: true,
        },
        {
            title: "模板名称",
            dataIndex: "name",
            key: "name",
            width: 240,
            ellipsis: true,
            render: (name, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>{name}</span>
                    <Tag size="small" color={record.is_system ? "blue" : "default"} style={{ fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                        {record.is_system ? "系统" : "自定义"}
                    </Tag>
                </div>
            ),
        },
        {
            title: "分类",
            dataIndex: "category",
            key: "category",
            width: 100,
            render: (category) => getCategoryText(category),
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 50,
            render: (status) => getStatusTag(status),
        },
        {
            title: "标签",
            dataIndex: "tags",
            key: "tags",
            width: 180,
            render: (tags) => (
                <div>
                    {tags && tags.length > 0 ? (
                        tags.slice(0, 3).map((tag, index) => (
                            <Tag key={index} size="small" style={{ marginBottom: 2 }}>
                                {tag}
                            </Tag>
                        ))
                    ) : (
                        <span style={{ color: "#999", fontSize: "12px" }}>无标签</span>
                    )}
                    {tags && tags.length > 3 && (
                        <Tag size="small" style={{ marginBottom: 2 }}>
                            +{tags.length - 3}
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 100,
            render: (time) => new Date(time).toLocaleString(),
        },
        {
            title: "操作",
            key: "actions",
            width: 200,
            fixed: "right",
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="查看详情">
                        <Button type="text" icon={<EyeOutlined />} onClick={() => handleView(record)} />
                    </Tooltip>

                    {!record.is_system && (
                        <Tooltip title="编辑">
                            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        </Tooltip>
                    )}

                    <Tooltip title="复制">
                        <Button type="text" icon={<CopyOutlined />} onClick={() => handleCopy(record)} />
                    </Tooltip>

                    {!record.is_system && (
                        <Popconfirm title="确定要删除这个模板吗？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                            <Tooltip title="删除">
                                <Button type="text" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">邮件模板管理</div>
                <div className="page-action-compact">
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        新建模板
                    </Button>
                </div>
            </div>

            {/* 筛选条件 */}
            <Card className="filter-card">
                <Row gutter={16}>
                    <Col span={6}>
                        <Input.Search placeholder="搜索模板名称或代码" allowClear onSearch={handleSearch} style={{ width: "100%" }} />
                    </Col>
                    <Col span={6}>
                        <Select mode="multiple" placeholder="选择标签" allowClear style={{ width: "100%" }} value={filters.tags} onChange={(value) => handleFilterChange("tags", value)} maxTagCount={2} maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}个标签`}>
                            {availableTags.map((tag) => (
                                <Option key={tag} value={tag}>
                                    {tag}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select placeholder="选择分类" allowClear style={{ width: "100%" }} value={filters.category} onChange={(value) => handleFilterChange("category", value)}>
                            {categories.map((category) => (
                                <Option key={category} value={category}>
                                    {getCategoryText(category)}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select placeholder="选择状态" allowClear style={{ width: "100%" }} value={filters.status} onChange={(value) => handleFilterChange("status", value)}>
                            <Option value="ACTIVE">启用</Option>
                            <Option value="INACTIVE">禁用</Option>
                            <Option value="DRAFT">草稿</Option>
                        </Select>
                    </Col>
                    <Col span={4}>
                        <Select placeholder="模板类型" allowClear style={{ width: "100%" }} value={filters.is_public} onChange={(value) => handleFilterChange("is_public", value)}>
                            <Option value={true}>公共模板</Option>
                            <Option value={false}>私有模板</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* 邮件模板列表 */}
            <Card className="data-card">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={templates}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* 模板编辑器 */}
            <EmailTemplateEditor
                visible={editorVisible}
                template={editingTemplate}
                onCancel={() => {
                    setEditorVisible(false);
                    setEditingTemplate(null);
                }}
                onSuccess={() => {
                    setEditorVisible(false);
                    setEditingTemplate(null);
                    loadTemplates();
                }}
            />

            {/* 模板详情 */}
            <EmailTemplateDetail
                visible={detailVisible}
                template={viewingTemplate}
                onCancel={() => {
                    setDetailVisible(false);
                    setViewingTemplate(null);
                }}
            />
        </div>
    );
};

export default EmailTemplateList;
